import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  HeartIcon,
  HomeIcon,
  LockClosedIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import Footer from '../components/common/Footer';
import Navigation from '../components/common/Navigation';
import ScrollToTop from '../components/common/ScrollToTop';

const HERO_IMAGE = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/images/community.webp`;

const pathways = [
  {
    title: 'Online banking',
    description: 'Secure access to balances, transfers, and statements anytime.',
    Icon: LockClosedIcon,
    to: '/login',
    tone: 'from-oak-800 to-oak-950',
  },
  {
    title: 'Accounts & loans',
    description: 'Checking, savings, mortgages, and personal lending options.',
    Icon: BuildingLibraryIcon,
    to: '/products',
    tone: 'from-claire-700 to-claire-900',
  },
  {
    title: 'Talk with us',
    description: 'Local specialists for membership questions and applications.',
    Icon: HeartIcon,
    to: '/contact',
    tone: 'from-oak-700 to-claire-800',
  },
];

const pillars = [
  {
    title: 'Everyday banking',
    description: 'Checking and savings shaped around how you spend and plan, not shareholder agendas.',
    points: ['Free checking options', 'Competitive savings', 'Mobile deposit'],
    Icon: BuildingLibraryIcon,
  },
  {
    title: 'Lending nearby',
    description: 'Auto, home, and personal loans with local decisions and straightforward terms.',
    points: ['Mortgages & refinancing', 'Auto & personal loans', 'Business lending'],
    Icon: HomeIcon,
  },
  {
    title: 'Banking that travels',
    description: 'Balances, bill pay, and transfers wherever you are, with people nearby when you need them.',
    points: ['24/7 digital access', 'Secure messaging', 'Branch & phone support'],
    Icon: DevicePhoneMobileIcon,
  },
];

const steps = [
  { n: '01', title: 'Apply online', body: 'Share a few details and start your membership application in minutes.' },
  { n: '02', title: 'We review', body: 'Our team verifies your information, usually within one to two business days.' },
  { n: '03', title: 'Bank with us', body: 'Open accounts, set up digital banking, and put Claireoaks tools to work.' },
];

const voices = [
  {
    quote: 'Claireoaks worked with us on a line of credit when we were ready to grow. Real people, clear answers.',
    name: 'Sarah Johnson',
    role: 'Small business owner',
  },
  {
    quote: 'The mortgage team walked us through every step. We always knew what to expect next.',
    name: 'Michael Chen',
    role: 'First-time homebuyer',
  },
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-mist text-oak-900">
      <Navigation />

      <header className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-grain">
        <img
          src={HERO_IMAGE}
          alt="Claireoaks Credit Union members and community"
          className="absolute inset-0 h-full w-full object-cover animate-ken-burns"
        />
        <div className="absolute inset-0 bg-oak-canopy" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-oak-950 via-oak-950/50 to-oak-950/15" />
        <div className="pointer-events-none absolute left-1/4 top-1/3 h-64 w-64 rounded-full bg-claire-400/20 blur-3xl animate-pulse-soft" />
        <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-oak-400/15 blur-3xl animate-drift" />

        <div className="relative mx-auto w-full max-w-7xl px-4 pb-20 pt-32 sm:px-6 sm:pb-24 lg:px-8 lg:pb-28">
          <div className="section-rule mb-7 animate-rise" />
          <p className="animate-rise font-display text-5xl font-semibold tracking-tight text-white sm:text-6xl md:text-7xl lg:text-8xl">
            Claireoaks
          </p>
          <h1 className="animate-rise animation-delay-300 mt-5 max-w-2xl font-display text-2xl font-medium leading-snug text-claire-100 sm:text-3xl md:text-4xl">
            Clear banking, rooted in community.
          </h1>
          <p className="animate-rise animation-delay-500 mt-6 max-w-lg text-base leading-relaxed text-oak-100/90 sm:text-lg">
            A member-owned credit union. Profits return as better rates, lower fees, and investments where our
            members live.
          </p>
          <div className="animate-rise animation-delay-500 mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center bg-white px-8 py-3.5 text-base font-semibold text-oak-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-claire-50"
            >
              Become a member
              <ArrowRightIcon className="ml-2 h-5 w-5" aria-hidden />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center border border-white/40 bg-white/5 px-8 py-3.5 text-base font-semibold text-white backdrop-blur-sm transition hover:bg-white/15"
            >
              Member sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Pathway panels */}
      <section className="relative z-10 -mt-10 px-4 sm:-mt-14 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-3 md:gap-5">
          {pathways.map(({ title, description, Icon, to, tone }) => (
            <Link
              key={title}
              to={to}
              className={`group relative overflow-hidden bg-gradient-to-br ${tone} p-7 text-white shadow-lg transition duration-500 hover:-translate-y-1 hover:shadow-xl`}
            >
              <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl transition group-hover:scale-150" />
              <Icon className="relative h-8 w-8 text-claire-200" aria-hidden />
              <h2 className="relative mt-5 font-display text-xl font-semibold">{title}</h2>
              <p className="relative mt-2 text-sm leading-relaxed text-white/80">{description}</p>
              <span className="relative mt-5 inline-flex items-center text-sm font-semibold text-claire-100">
                Continue
                <ArrowRightIcon className="ml-1.5 h-4 w-4 transition group-hover:translate-x-1" aria-hidden />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Membership pillars */}
      <section className="relative overflow-hidden bg-leaf-vein py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="section-rule mb-5" />
              <h2 className="font-display text-3xl font-semibold tracking-tight text-oak-900 sm:text-4xl lg:text-5xl">
                One relationship for your money
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-oak-600">
                From your first account to your next home, Claireoaks grows with you, without the noise of a big bank.
              </p>
            </div>
            <Link to="/products" className="link-underline shrink-0 pb-1">
              Explore accounts &amp; loans
              <ArrowRightIcon className="ml-2 h-4 w-4" aria-hidden />
            </Link>
          </div>

          <div className="mt-16 grid gap-6 lg:grid-cols-3">
            {pillars.map(({ title, description, points, Icon }) => (
              <article
                key={title}
                className="panel-lift group border border-oak-100/80 p-8 lg:p-9"
              >
                <div className="flex h-12 w-12 items-center justify-center bg-oak-900 text-claire-200 transition duration-500 group-hover:bg-claire-700">
                  <Icon className="h-6 w-6" aria-hidden />
                </div>
                <h3 className="mt-7 font-display text-2xl font-semibold text-oak-900">{title}</h3>
                <p className="mt-3 leading-relaxed text-oak-600">{description}</p>
                <ul className="mt-6 space-y-2.5 border-t border-oak-100 pt-6">
                  {points.map((point) => (
                    <li key={point} className="flex items-start gap-2.5 text-sm text-oak-700">
                      <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-claire-600" aria-hidden />
                      {point}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Featured rate / product band */}
      <section className="relative overflow-hidden bg-oak-950 py-20 text-white sm:py-24">
        <div className="pointer-events-none absolute inset-0 bg-pattern-canopy opacity-70" />
        <div className="pointer-events-none absolute right-0 top-0 h-full w-1/2 bg-gradient-to-l from-claire-800/40 to-transparent" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-claire-300">Featured for members</p>
            <h2 className="mt-4 font-display text-3xl font-semibold sm:text-4xl lg:text-5xl">
              Grow quietly.
              <span className="mt-2 block text-claire-200">Borrow locally.</span>
            </h2>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-oak-200">
              Competitive savings and loan options backed by people who know your community, not a distant call center.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/products"
                className="inline-flex items-center bg-white px-6 py-3 text-sm font-semibold text-oak-900 transition hover:bg-claire-50"
              >
                View products
                <ArrowRightIcon className="ml-2 h-4 w-4" aria-hidden />
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center border border-white/30 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Open an account
              </Link>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="border border-white/10 bg-white/5 p-7 backdrop-blur-sm">
              <ChartBarIcon className="h-7 w-7 text-claire-300" aria-hidden />
              <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-claire-200/80">Savings</p>
              <p className="mt-2 font-display text-2xl font-semibold">High-yield options</p>
              <p className="mt-2 text-sm text-oak-200">Competitive APY on qualifying balances. Ask us for today’s rates.</p>
            </div>
            <div className="border border-white/10 bg-white/5 p-7 backdrop-blur-sm sm:mt-8">
              <SparklesIcon className="h-7 w-7 text-claire-300" aria-hidden />
              <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-claire-200/80">Auto loans</p>
              <p className="mt-2 font-display text-2xl font-semibold">Same-day decisions</p>
              <p className="mt-2 text-sm text-oak-200">Pre-qualify online. Local underwriting when every hour counts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="section-rule mb-5" />
            <h2 className="font-display text-3xl font-semibold text-oak-900 sm:text-4xl">Membership in three steps</h2>
            <p className="mt-4 text-lg text-oak-600">Clear process. Local people reviewing every application.</p>
          </div>
          <div className="mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
            {steps.map((step, i) => (
              <div key={step.n} className="relative">
                {i < steps.length - 1 ? (
                  <div className="pointer-events-none absolute left-[4.5rem] top-8 hidden h-px w-[calc(100%-2rem)] bg-gradient-to-r from-oak-200 to-transparent md:block" />
                ) : null}
                <p className="font-display text-5xl font-semibold text-oak-100">{step.n}</p>
                <h3 className="mt-4 font-display text-xl font-semibold text-oak-900">{step.title}</h3>
                <p className="mt-3 leading-relaxed text-oak-600">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Voices */}
      <section className="border-y border-oak-100 bg-mist py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="section-rule mb-5" />
              <h2 className="font-display text-3xl font-semibold text-oak-900 sm:text-4xl">Trusted by our community</h2>
            </div>
            <p className="max-w-md text-oak-600">Real members. Real neighborhoods. Banking that still feels human.</p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {voices.map((v) => (
              <blockquote
                key={v.name}
                className="relative overflow-hidden border border-oak-100 bg-white p-8 shadow-soft sm:p-10"
              >
                <span className="pointer-events-none absolute -right-2 -top-6 font-display text-8xl text-oak-50">
                  &ldquo;
                </span>
                <p className="relative text-xl leading-relaxed text-oak-800 sm:text-2xl">&ldquo;{v.quote}&rdquo;</p>
                <footer className="relative mt-8 flex items-center gap-4 border-t border-oak-100 pt-6">
                  <div className="flex h-11 w-11 items-center justify-center bg-oak-900 font-display text-sm font-semibold text-claire-200">
                    {v.name
                      .split(' ')
                      .map((p) => p[0])
                      .join('')}
                  </div>
                  <div>
                    <cite className="not-italic font-semibold text-oak-900">{v.name}</cite>
                    <p className="text-sm text-oak-500">{v.role}</p>
                  </div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Community */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[480px] lg:min-h-[560px]">
            <img
              src="/images/community.webp"
              alt="Claireoaks members and neighbors at a local community gathering"
              className="absolute inset-0 h-full w-full object-cover transition duration-[1.2s] hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-oak-950/50 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-oak-950/20" />
          </div>
          <div className="relative flex flex-col justify-center overflow-hidden bg-oak-900 px-8 py-20 sm:px-12 lg:px-16 lg:py-28">
            <div className="pointer-events-none absolute -right-20 top-10 h-56 w-56 rounded-full bg-claire-500/20 blur-3xl" />
            <HeartIcon className="relative h-9 w-9 text-claire-300" aria-hidden />
            <h2 className="relative mt-6 font-display text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">
              We invest where members live
            </h2>
            <p className="relative mt-5 max-w-md text-lg leading-relaxed text-oak-200">
              Sponsorships, education, and volunteer time are part of how we operate, not a seasonal campaign. Your
              membership strengthens the same neighborhoods we share.
            </p>
            <div className="relative mt-10 grid max-w-sm grid-cols-2 gap-6 border-t border-white/10 pt-8">
              <div>
                <p className="font-display text-3xl font-semibold text-white">$5M+</p>
                <p className="mt-1 text-sm text-oak-300">Community support</p>
              </div>
              <div>
                <p className="font-display text-3xl font-semibold text-white">10k+</p>
                <p className="mt-1 text-sm text-oak-300">Volunteer hours</p>
              </div>
            </div>
            <Link to="/about" className="relative mt-10 inline-flex items-center font-semibold text-claire-200 transition hover:text-white">
              Our story
              <ArrowRightIcon className="ml-2 h-5 w-5" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative overflow-hidden bg-leaf-vein py-24 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <p className="font-display text-3xl font-semibold text-claire-700 sm:text-4xl">Claireoaks</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-oak-900 sm:text-5xl">Ready when you are</h2>
          <p className="mt-5 text-lg text-oak-600 sm:text-xl">
            Open membership online in minutes, or reach out. We will help you choose the right accounts.
          </p>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center bg-oak-900 px-8 py-3.5 text-base font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-claire-700"
            >
              Start membership
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center border border-oak-300 bg-white/70 px-8 py-3.5 text-base font-semibold text-oak-800 backdrop-blur-sm transition hover:border-oak-500 hover:bg-white"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default HomePage;
